import { QueryClient, MutationCache } from '@tanstack/react-query';
import {
  setMutationLock,
  clearMutationLock,
} from '@/shared/api/websocket/realtime-sync.locks';

// Entities whose WebSocket events trigger cache invalidation (non-instant)
// or instant handlers. Mutations on these entities auto-acquire a lock so
// that a cross-device WebSocket event doesn't overwrite in-flight
// optimistic state. Source of truth: `realtime-sync.handlers.ts`
// `onInstant(...)` registrations.
const LOCKABLE_ENTITIES = new Set([
  'projects',
  'concepts',
  'angles',
  'concept_angles',
  'references',
  'controls',
  'renders',
  'albums',
  'users',
  'subscriptions',
  'payments',
  'balance',
]);

const POST_SETTLEMENT_BUFFER_MS = 2000;

// Resolve the entity id used for the WS lock. Static `mutationKey[2]` wins;
// otherwise we read it from `meta.lockId(variables)` since most hooks only
// know the id at `mutate(...)` time. Returns `null` when there's no id —
// callers (creates, generic ops) get no lock, which is safe: there's no
// existing record yet to protect from a racing WS event.
function resolveLockId(
  mutation: { options: { mutationKey?: readonly unknown[]; meta?: unknown } },
  variables: unknown
): string | null {
  const key = mutation.options.mutationKey;
  if (key && key.length >= 3 && key[2] != null) return String(key[2]);
  const meta = mutation.options.meta as
    | { lockId?: (vars: unknown) => string | undefined | null }
    | undefined;
  const fromMeta = meta?.lockId?.(variables);
  return fromMeta ?? null;
}

const mutationCache = new MutationCache({
  onMutate: (variables, mutation) => {
    const key = mutation.options.mutationKey;
    if (!key || key.length < 1) return;
    const entity = key[0] as string;
    if (!LOCKABLE_ENTITIES.has(entity)) return;
    const id = resolveLockId(mutation, variables);
    if (!id) return;
    setMutationLock(entity, 10_000, id);
  },
  onSettled: (_data, _error, variables, _context, mutation) => {
    const key = mutation.options.mutationKey;
    if (!key || key.length < 1) return;
    const entity = key[0] as string;
    if (!LOCKABLE_ENTITIES.has(entity)) return;
    const id = resolveLockId(mutation, variables);
    if (!id) return;
    // Keep lock briefly after settlement for WebSocket event propagation delay
    setTimeout(() => clearMutationLock(entity, id), POST_SETTLEMENT_BUFFER_MS);
  },
  // No `onError` here on purpose. TanStack's `MutationCache.onError` fires
  // independently of (and in addition to) any caller's `try/catch await
  // mutateAsync()` and per-mutation `onError` — and there's no clean signal
  // that a caller is already handling the error. So a global toaster here
  // double-surfaces (call sites already route limits to paywalls and toast
  // in their own `catch`). The
  // single seam is the per-call-site `useLimitErrorPaywall` hook (or the
  // extracted non-hook `routeLimitErrorToPaywall` when outside React);
  // mutations without an `onError` either propagate via `mutateAsync`
  // rejection to a `try/catch` or are intentionally fire-and-forget.
});

export const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      // Always attempt the request instead of pausing on the browser's own
      // offline verdict. TanStack Query's default networkMode 'online' gates
      // fetches on navigator.onLine, which is notoriously wrong: it reports
      // false on live connections behind VPNs/proxies/captive portals and in
      // environments where the browser's connectivity probe is blocked, leaving
      // queries stuck in 'paused' with no real failure. We treat offline as an
      // actual fetch failure (ApiClientError status 0) instead — matching the
      // Apple/Android clients, which likewise never gate on a platform verdict.
      networkMode: 'always',
      // Stale time: 5 minutes - reasonable default for most API queries
      // Individual queries can override this as needed
      staleTime: 5 * 60 * 1000,
      // Cache time: 30 minutes - keep data in memory for background refetches
      gcTime: 30 * 60 * 1000,
      // Retry failed requests 3 times with exponential backoff
      retry: (failureCount, error: unknown) => {
        // Don't retry an aborted request — cancellation is intentional (the
        // caller navigated away or a newer query superseded it). Retrying would
        // re-fire 3x and re-log it as a phantom failure.
        if (error instanceof DOMException && error.name === 'AbortError') {
          return false;
        }
        // Don't retry on 4xx errors
        const errorWithStatus = error as { status?: number; timeout?: boolean };
        if (
          errorWithStatus?.status &&
          errorWithStatus.status >= 400 &&
          errorWithStatus.status < 500
        ) {
          return false;
        }
        // Deadline breach (ApiClientError.timeout): each attempt costs a full
        // 20s before it even fails, so the default 3 retries would keep a
        // loading screen up for over a minute. One retry covers a transient
        // wedge; beyond that the user is better served by a visible error +
        // Retry.
        if (errorWithStatus?.timeout === true) {
          return failureCount < 1;
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Don't refetch on window focus - rely on explicit invalidation
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Fire mutations regardless of the browser's navigator.onLine verdict
      // (see queries.networkMode above) — a false offline reading must not
      // silently pause a user's Generate/Save/Create action.
      networkMode: 'always',
      // Disable automatic retries for mutations
      retry: 0,
    },
  },
});

// Error handler for queries and mutations
export const handleQueryError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String(error.message);
  }
  return 'An unexpected error occurred';
};
